import { ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '@/shared/auth';
import { azureConfigured } from '@/shared/msal';
import { AgentFlow } from '../components/AgentFlow';
import { AXIS_LOGO_DATA_URI } from '@/shared/axisLogo';

/**
 * Login page — full-bleed two-column Liquid Glass on desktop, stacks
 * into a normal document flow on mobile.
 *
 * Layout
 * ------
 *
 * Desktop (md ≥ 768px): two columns side-by-side, both ≥ min-h-screen
 * so the aurora background and the sign-in card both fill the viewport.
 *
 * Mobile (<768px): stacks. Left column (hero + AgentFlow + status)
 * renders first, then the sign-in card below. Container drops the
 * min-h-screen constraint so content flows naturally — no viewport
 * pinning that crushes content on short phones or stretches it on
 * tall ones. The sign-in card no longer vertically centers, so it
 * doesn't end up behind a mobile keyboard.
 */
export function LoginPage() {
  const { signIn } = useAuth();

  return (
    <div className="relative min-h-screen w-full overflow-hidden md:flex">
      {/* Aurora background blobs — three drifting color washes.
          Sized in viewport-width units so they scale down sensibly on
          phones (a fixed 720px blob on a 360px screen dominates the
          paint and burns GPU). Pinned to the larger of {dimension, 320px}
          so they don't disappear on extremely narrow viewports. */}
      <div
        className="aurora-blob"
        style={{
          top: '-15%', left: '-10%',
          width: 'max(320px, 70vw)', height: 'max(320px, 70vw)',
          background: 'radial-gradient(circle, var(--aurora-1) 0%, transparent 70%)',
          animationDelay: '0s',
        }}
      />
      <div
        className="aurora-blob"
        style={{
          top: '30%', right: '-20%',
          width: 'max(320px, 80vw)', height: 'max(320px, 80vw)',
          background: 'radial-gradient(circle, var(--aurora-2) 0%, transparent 70%)',
          animationDelay: '-6s',
        }}
      />
      <div
        className="aurora-blob"
        style={{
          bottom: '-25%', left: '15%',
          width: 'max(280px, 60vw)', height: 'max(280px, 60vw)',
          background: 'radial-gradient(circle, var(--aurora-3) 0%, transparent 70%)',
          animationDelay: '-12s',
        }}
      />

      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, var(--glass-border-h) 1px, transparent 1px), linear-gradient(to bottom, var(--glass-border-h) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          opacity: 0.35,
        }}
      />

      <div className="relative z-10 flex flex-col md:flex-row w-full md:min-h-screen">
        {/* ─── LEFT — Hero + AgentFlow ─────────────────────────────────── */}
        {/* gap-y-10 on mobile gives the three sub-blocks (wordmark, hero,
            status) honest vertical spacing instead of justify-between
            stretching them across the viewport. flex-col + gap is
            simpler than absolute height math. */}
        <div className="flex-1 flex flex-col gap-y-6 md:gap-y-4 md:justify-between px-6 sm:px-12 lg:px-16 py-6 md:py-6">
          {/* Top: wordmark */}
          <div className="flex items-center gap-3" style={{ animation: 'fadeUp 0.6s 0s both' }}>
            <BackofficeMark />
            <div className="leading-none">
              <div className="text-base font-semibold tracking-tight text-ink-strong">Orchestrator</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400 mt-0.5">v0.1.0 · Live</div>
            </div>
          </div>

          {/* Middle: headline + sub + AgentFlow */}
          <div className="max-w-2xl">
            <h1
              className="text-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight md:tracking-tighter leading-[1.05] md:leading-[0.95] text-ink-strong"
              style={{ animation: 'fadeUp 0.7s 0.2s both' }}
            >
              Orchestration,<br />
              <span className="text-shimmer">at the speed of thought.</span>
            </h1>

            {/* AgentFlow — the 5-stage pipeline. Responsive: a vertical
                stack on phones, a horizontal flow on tablet/desktop. */}
            <div className="block mt-6 md:mt-6" style={{ animation: 'fadeUp 0.7s 0.4s both' }}>
              <AgentFlow />
            </div>
          </div>

          {/* Bottom: status + footer */}
          <div className="flex flex-wrap items-center justify-between gap-y-2 text-xs text-ink-400 tracking-tight md:pt-2" style={{ animation: 'fadeUp 0.7s 0.6s both' }}>
            <div className="inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-status-ok animate-pulse-dot" />
              All systems operational
            </div>
            <div className="hidden sm:block">
              Secured by Microsoft Entra ID · TLS 1.3
            </div>
          </div>
        </div>

        {/* ─── RIGHT — Sign-in card ────────────────────────────────────── */}
        {/* On mobile this just continues below the left column in normal
            flow. items-center is desktop-only — on mobile the card sits
            in document order, so users with on-screen keyboards still
            see the sign-in button above the keyboard. */}
        <div
          className="md:w-[480px] lg:w-[520px] flex md:items-center justify-center px-6 sm:px-12 py-10 md:py-10 relative"
          style={{
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            // Border between columns: bottom-edge on mobile (where the
            // card stacks below), left-edge on desktop (where it's
            // alongside).
            borderTop: '1px solid var(--glass-border)',
            boxShadow: 'inset 0 1px 0 0 var(--glass-edge)',
          }}
        >
          <div className="w-full max-w-sm" style={{ animation: 'fadeUp 0.7s 0.45s both' }}>
            <div className="mb-8">
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400 mb-2">Sign in</div>
              <h2 className="text-4xl font-bold tracking-tighter leading-none">
                <span className="text-shimmer">Welcome</span>
              </h2>
              <p className="mt-2 text-sm text-ink-200 tracking-tight">
                By signing in you agree to your organization's acceptable use policy.
              </p>
            </div>

            {azureConfigured ? (
              <>
                <button
                  type="button"
                  onClick={signIn}
                  className="group w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-2xl
                             text-base font-semibold tracking-tight text-white
                             transition duration-200 ease-out
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  style={{
                    background: 'linear-gradient(135deg, var(--accent-glow) 0%, var(--accent) 100%)',
                    boxShadow: 'var(--accent-shadow), inset 0 1px 0 0 rgba(255,255,255,0.20)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <MicrosoftLogo />
                  <span>Continue with Microsoft</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>

                <div className="mt-6 space-y-2.5">
                  <Bullet>Single sign-on via Azure AD</Bullet>
                  <Bullet>Role-based access for maker / checker / admin</Bullet>
                  <Bullet>Every action audit-logged for compliance</Bullet>
                </div>
              </>
            ) : (
              <div
                className="flex gap-3 p-4 rounded-xl text-sm"
                style={{ background: 'color-mix(in srgb, var(--status-warn) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--status-warn) 30%, transparent)' }}
              >
                <AlertCircle className="w-5 h-5 shrink-0" style={{ color: 'var(--status-warn)' }} />
                <div className="text-ink-200">
                  <div className="font-semibold text-ink-strong">Microsoft sign-in is not configured.</div>
                  <div className="text-xs mt-1 text-ink-400">
                    Ask an administrator to set <code>AZURE_TENANT_ID</code> and <code>AZURE_CLIENT_ID</code>.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 text-sm text-ink-200 tracking-tight">
      <span
        className="inline-block w-1.5 h-1.5 rounded-full mt-2 shrink-0"
        style={{ background: 'var(--accent)' }}
      />
      <span>{children}</span>
    </div>
  );
}

function BackofficeMark() {
  // Axis "A" logo mark, embedded as a data URI so it always loads without
  // depending on a separate asset file. Rendered at the same 36x36 size as
  // the previous mark.
  return (
    <img
      src={AXIS_LOGO_DATA_URI}
      width={36}
      height={36}
      alt="Axis"
      style={{ width: 36, height: 36, objectFit: 'contain', display: 'block' }}
    />
  );
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="10" height="10" x="1"  y="1"  fill="#F25022" />
      <rect width="10" height="10" x="12" y="1"  fill="#7FBA00" />
      <rect width="10" height="10" x="1"  y="12" fill="#00A4EF" />
      <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
    </svg>
  );
}
